import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  statPairFiguresRecipe,
  statPairRecipe,
} from '../recipes/stat-card.recipe';
import { Icon, type IconComponent } from './Icon';
import { Divider, Inline, Stack } from './Layout';
import { Loading } from './Spinner';
import { Text } from './Text';

export interface StatPairItem {
  /** Plain text: it also names the spinner while the number loads. */
  label: string;
  /** Already formatted — the section does no arithmetic. */
  value: React.ReactNode;
  loading?: boolean;
  /** A line under the number: "$85,980 due soon", "$17,001 is overdue". */
  note?: React.ReactNode;
  /** `destructive` for a note that is a problem: money overdue. */
  noteTone?: 'muted' | 'destructive';
}

export interface StatPairProps extends React.HTMLAttributes<HTMLElement> {
  /** What the numbers are about: "Calls", "Invoices". */
  title?: string;
  /** The glyph before the title. */
  icon?: IconComponent;
  /** One number, or two side by side with a rule between them. */
  stats: [StatPairItem] | [StatPairItem, StatPairItem];
}

/**
 * A section of the phone's Home: "Calls" with today's and the completed
 * count side by side, "Invoices" with what was invoiced and paid.
 *
 *   <Stack gap={0} divided>
 *     <StatPair title="Calls" icon={PhoneCall} stats={[
 *       { label: 'Today', value: 12 },
 *       { label: 'Completed', value: 9, loading },
 *     ]} />
 *   </Stack>
 *
 * The Figma's Home card: the heading muted with its glyph, each label at the
 * large body size over its number at heading size, no card around it. Stack
 * several with `divided` and they are ruled off as the Figma draws them. For
 * a number with a trend on a desk, use StatCard; for three, StatGrid.
 */
export const StatPair = React.forwardRef<HTMLElement, StatPairProps>(
  function StatPair({ title, icon, stats, className, ...props }, ref) {
    const figures = stats.map((stat) => (
      <Stack key={stat.label} gap={4}>
        <Stack gap={0}>
          <Text variant="body-strong">{stat.label}</Text>
          <Text variant="h1" numeric>
            {stat.loading ? (
              <Loading
                label={`Loading ${stat.label.toLowerCase()}`}
                pad="none"
              />
            ) : (
              (stat.value ?? '0')
            )}
          </Text>
        </Stack>
        {stat.note ? (
          <Text
            variant="body-sm"
            tone={stat.noteTone === 'destructive' ? 'destructive' : 'muted'}
          >
            {stat.note}
          </Text>
        ) : null}
      </Stack>
    ));
    return (
      <section ref={ref} className={cn(statPairRecipe(), className)} {...props}>
        {title ? (
          <Inline gap={1} align="center">
            {icon ? (
              <Icon icon={icon} weight="fill" size="lg" tone="muted" />
            ) : null}
            <Text as="h2" variant="h3" tone="muted">
              {title}
            </Text>
          </Inline>
        ) : null}
        <div className={statPairFiguresRecipe()}>
          {figures[0]}
          {figures[1] ? (
            <>
              <Divider orientation="vertical" />
              {figures[1]}
            </>
          ) : null}
        </div>
      </section>
    );
  }
);
